import { useState } from "react";

/*
    The internal id of the thing you are looking at (MPN-1635).

    These ids are how records get referred to in tickets, cleanup lists and support
    threads, and the only way to read one was to pick it out of the URL -- which does not
    say which number is which. Cross-referencing a bare number from a cleanup list meant
    checking it against the engagement, the contract, the population and the campaign
    before finding the match.

    One component for all six entities so the label is always present and always in the
    same place. Six hand-rolled spans would drift, and "ID 149" with no noun is the
    problem this is fixing, not a smaller version of it.

    Visible, not behind a hover or a menu: the ticket is explicit, and something you have
    to discover is no better than the URL. Clicking copies, which is what anyone reading
    an id is about to do by hand anyway -- an affordance on top of the visible text, never
    a replacement for it.
*/

interface RecordIdProps {
  /** The noun, e.g. "Engagement". Rendered as "Engagement ID 149". */
  label: string;
  id: number | string | null | undefined;
  className?: string;
}

export function RecordId({ label, id, className }: RecordIdProps) {
  const [copied, setCopied] = useState(false);

  // A record with no id is one that has not been saved; there is nothing to cross
  // reference, and "Engagement ID —" is noise on a page that has not loaded.
  if (id === null || id === undefined || id === "") return null;

  const text = String(id);

  const copy = () => {
    // Best effort. Clipboard access is denied in some embedded contexts, and the id is
    // already on screen to be read, so a failure changes nothing that matters.
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      () => {},
    );
  };

  return (
    <button
      type="button"
      onClick={copy}
      className={`record-id${className ? ` ${className}` : ""}`}
      title={copied ? "Copied" : `Copy ${label} ID ${text}`}
      aria-label={`${label} ID ${text}`}
      data-testid="record-id"
    >
      <span className="record-id__label">{label} ID</span>
      <span className="record-id__value">{text}</span>
      {copied && <span className="record-id__copied" aria-hidden="true">copied</span>}
    </button>
  );
}
